import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Download, FileSpreadsheet, Calendar, Filter } from 'lucide-react';
import { toast } from 'sonner';
import { getApiUrl } from '../utils/api';
import { gatedDownload } from '../lib/gatedDownload';

const API_URL = getApiUrl();

const ExportPage = () => {
  const [dateRange, setDateRange] = useState({
    start_date: '',
    end_date: ''
  });
  const [paymentStatus, setPaymentStatus] = useState('all');
  const [salesRep, setSalesRep] = useState('all');
  const [salesUsers, setSalesUsers] = useState([]);
  const [downloading, setDownloading] = useState(null);

  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const res = await axios.get(`${API_URL}/api/users`, { withCredentials: true });
        const users = (res.data || []).filter(u =>
          ['super_admin', 'sales', 'pre_sales'].includes(u.role) && u.status === 'active'
        );
        setSalesUsers(users);
      } catch (err) {
        console.error('Failed to fetch users:', err);
      }
    };
    fetchUsers();
  }, []);

  const downloadCSV = async (type) => {
    setDownloading(type);
    try {
      let url = `/api/export/${type}/csv`;
      const params = new URLSearchParams();
      
      if (dateRange.start_date) params.append('start_date', dateRange.start_date);
      if (dateRange.end_date) params.append('end_date', dateRange.end_date);
      if (type === 'payments' && paymentStatus !== 'all') {
        params.append('status', paymentStatus);
      }
      if (type === 'payments' && salesRep !== 'all') {
        params.append('sales_rep', salesRep);
      }
      
      if (params.toString()) {
        url += `?${params.toString()}`;
      }

      const saved = await gatedDownload(url, `${type}_export.csv`);
      if (saved) toast.success(`${type.charAt(0).toUpperCase() + type.slice(1)} exported successfully`);
    } catch (error) {
      console.error('Export error:', error);
      toast.error(`Failed to export ${type}`);
    } finally {
      setDownloading(null);
    }
  };

  const exportTypes = [
    {
      id: 'payments',
      title: 'Payments Report',
      description: 'All payment transactions with customer details, amounts, GST breakdown, and sales rep info',
      icon: '💰',
      fields: ['Date', 'Invoice No', 'Customer', 'Amount', 'GST', 'Status', 'Sales Rep']
    },
    {
      id: 'invoices',
      title: 'Invoices Report',
      description: 'GST-compliant invoice data with CGST/SGST split for Tally import',
      icon: '📄',
      fields: ['Invoice Date', 'Invoice No', 'Customer', 'GSTIN', 'Base Amount', 'CGST', 'SGST', 'Total']
    },
    {
      id: 'commissions',
      title: 'Doctor Commissions',
      description: 'Commission payouts for doctor referrals with enrollment details',
      icon: '👨‍⚕️',
      fields: ['Date', 'Doctor', 'Lead', 'Enrollment Amount', 'Commission %', 'Commission Amount', 'Status']
    }
  ];

  return (
    <div data-testid="export-page" className="space-y-6 animate-fade-in">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-slate-900">Export Reports</h1>
        <p className="text-slate-500">Download data for Tally import and accounting</p>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
        <h3 className="font-semibold text-slate-900 mb-4 flex items-center gap-2">
          <Filter className="w-5 h-5 text-slate-400" />
          Filter Options
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Start Date</label>
            <input
              type="date"
              value={dateRange.start_date}
              onChange={(e) => setDateRange({ ...dateRange, start_date: e.target.value })}
              className="input-base"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">End Date</label>
            <input
              type="date"
              value={dateRange.end_date}
              onChange={(e) => setDateRange({ ...dateRange, end_date: e.target.value })}
              className="input-base"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Payment Status</label>
            <select
              value={paymentStatus}
              onChange={(e) => setPaymentStatus(e.target.value)}
              className="input-base"
            >
              <option value="all">All Statuses</option>
              <option value="paid">Paid</option>
              <option value="captured">Captured</option>
              <option value="pending">Pending</option>
              <option value="expired">Expired</option>
              <option value="refunded">Refunded</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Sales Associate</label>
            <select
              value={salesRep}
              onChange={(e) => setSalesRep(e.target.value)}
              className="input-base"
              data-testid="export-sales-rep-filter"
            >
              <option value="all">All Associates</option>
              {salesUsers.map(u => (
                <option key={u.user_id} value={u.user_id}>{u.full_name} ({u.role.replace('_', ' ')})</option>
              ))}
            </select>
          </div>
          <div className="flex items-end">
            <button
              onClick={() => {
                setDateRange({ start_date: '', end_date: '' });
                setPaymentStatus('all');
                setSalesRep('all');
              }}
              className="btn-secondary w-full"
            >
              Clear Filters
            </button>
          </div>
        </div>
      </div>

      {/* Export Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {exportTypes.map((type) => (
          <div 
            key={type.id}
            className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden hover:shadow-md transition-shadow"
          >
            <div className="p-6">
              <div className="flex items-start justify-between mb-4">
                <div>
                  <span className="text-3xl mb-2 block">{type.icon}</span>
                  <h3 className="font-semibold text-slate-900">{type.title}</h3>
                </div>
                <FileSpreadsheet className="w-6 h-6 text-emerald-500" />
              </div>
              <p className="text-sm text-slate-600 mb-4">{type.description}</p>
              <div className="flex flex-wrap gap-1 mb-4">
                {type.fields.slice(0, 4).map((field, idx) => (
                  <span key={idx} className="px-2 py-0.5 bg-slate-100 text-slate-600 text-xs rounded">
                    {field}
                  </span>
                ))}
                {type.fields.length > 4 && (
                  <span className="px-2 py-0.5 bg-slate-100 text-slate-600 text-xs rounded">
                    +{type.fields.length - 4} more
                  </span>
                )}
              </div>
            </div>
            <div className="px-6 py-4 bg-slate-50 border-t border-slate-200">
              <button
                onClick={() => downloadCSV(type.id)}
                disabled={downloading === type.id}
                className="btn-primary w-full flex items-center justify-center gap-2"
                data-testid={`export-${type.id}-btn`}
              >
                {downloading === type.id ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    Exporting...
                  </>
                ) : (
                  <>
                    <Download className="w-4 h-4" />
                    Download CSV
                  </>
                )}
              </button>
            </div>
          </div>
        ))}
      </div>

      {/* Tally Import Instructions */}
      <div className="bg-blue-50 border border-blue-200 rounded-xl p-6">
        <h3 className="font-semibold text-blue-900 mb-3">📘 Tally Import Instructions</h3>
        <ol className="text-sm text-blue-800 space-y-2 list-decimal list-inside">
          <li>Download the required CSV report using the buttons above</li>
          <li>Open Tally Prime and go to <strong>Gateway of Tally → Import</strong></li>
          <li>Select <strong>Data → Masters/Vouchers</strong> as import type</li>
          <li>Browse and select the downloaded CSV file</li>
          <li>Map the columns to corresponding Tally fields</li>
          <li>Verify the preview and click Import to complete</li>
        </ol>
        <p className="text-xs text-blue-600 mt-3">
          Note: The Invoices report includes CGST/SGST split which is required for GST filing in Tally.
        </p>
      </div>
    </div>
  );
};

export default ExportPage;
